/**
 * /field/ndt/jobs/:jobId/welds/:weldId — one weld on the technician's phone,
 * with FAST EXAM ENTRY (POST /ndt-inspection/field/exams, fieldiq-api#1144).
 *
 * The weld comes from the job read (GET /field/my-jobs/:id carries every weld
 * with its exams), so there is no second request. SNAKE_CASE throughout.
 *
 * Fast entry: pick the method (and for RT the gamma source, if any), the
 * instrument, then ONE big button — Accept, Repair or Cut out — or "Shot only"
 * to leave it pending for a Level II to read. The technician is the caller
 * (the API defaults it and refuses anyone else, `not_self`), the cycle is the
 * API's next one, and the time is the API's now.
 *
 * THE SHOT GUARD IS THE API'S. A 409 lists every reason in words, in the place
 * the buttons are; a save may come back with warnings (due soon), shown after.
 * Writes are online only — a failed save says plainly it was not saved.
 */
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAddNdtFieldExam, useNdtCatalog, useNdtFieldJob, useNdtInstruments, useNdtSources } from '../../../hooks/useNdtInspection';
import { formatInstant } from '../../../lib/calendar-date';
import {
  formatNdtValue,
  ndtCycleLabel,
  ndtExamResultSkin,
  ndtKindLabel,
  ndtLevelLabel,
  ndtQualReasonText,
  ndtQualStatusSkin,
  ndtRefusal,
  ndtWarningText,
  ndtWeldStandingSkin,
  type NdtRefusal,
} from '../../../lib/ndt-inspection';
import type { NdtCatalog, NdtCatalogB2, NdtExamResult } from '../../../types/ndt-inspection';
import { NDT_INPUT, NdtField } from '../NdtBits';
import { NdtRefusalNotice, NdtWarnings } from '../NdtJobBits';
import {
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldEmpty,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
} from '../../pressure-testing/field/PtFieldBits';
import { NDT_FIELD_NOT_SAVED } from './NdtFieldJobScreen';

const BIG = 'flex h-16 w-full items-center justify-center rounded-fi-control border border-black/20 text-lg font-black uppercase tracking-[0.04em] text-fi-signal-ink disabled:cursor-not-allowed disabled:opacity-50';

export const NDT_FIELD_RESULT_BUTTONS: { result: NdtExamResult; label: string; fill: string }[] = [
  { result: 'accept', label: 'Accept', fill: 'bg-fi-signal-green' },
  { result: 'repair', label: 'Repair', fill: 'bg-fi-signal-amber' },
  { result: 'cutout', label: 'Cut out', fill: 'bg-fi-signal-red' },
];

export default function NdtFieldWeldScreen() {
  const { jobId, weldId } = useParams<{ jobId: string; weldId: string }>();
  const query = useNdtFieldJob(jobId);
  const job = query.data?.data ?? null;
  const meta = query.data?.meta;
  const weld = job?.welds?.find((w) => w.id === weldId) ?? null;
  const catalog = useNdtCatalog();
  const cat = catalog.data?.data as (NdtCatalog & NdtCatalogB2) | null | undefined;
  const add = useAddNdtFieldExam();
  const [method, setMethod] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [instrumentId, setInstrumentId] = useState('');
  const [refusal, setRefusal] = useState<NdtRefusal | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const requiredKind = useMemo(() => {
    const kinds = cat?.examInstrumentKinds ?? {};
    if (method === 'RT') return sourceId ? kinds.RT ?? null : null;
    return method ? kinds[method] ?? null : null;
  }, [cat, method, sourceId]);
  const instruments = useNdtInstruments(requiredKind ? { kind: requiredKind } : undefined);
  const sources = useNdtSources({ status: 'in_service' });

  const record = (result: NdtExamResult) => {
    if (!weld) return;
    setRefusal(null);
    setWarnings([]);
    if (!method) {
      setRefusal({ status: null, code: null, message: 'Choose the method first.', failures: [], warnings: [], weldCounts: null, expected: null });
      return;
    }
    add.mutate(
      {
        weld_id: weld.id,
        method,
        result,
        instrument_equipment_id: instrumentId || null,
        source_id: method === 'RT' && sourceId ? sourceId : null,
      },
      {
        onSuccess: (res) => {
          toast.success(result === 'pending' ? 'Shot recorded — pending' : `Recorded: ${ndtExamResultSkin(result).label}`);
          setWarnings(res.warnings.map(ndtWarningText));
        },
        onError: (err) => setRefusal(ndtRefusal(err, NDT_FIELD_NOT_SAVED)),
      },
    );
  };

  const qual = weld?.welder_qualification;
  const exams = weld?.exams ?? [];
  const unit = meta?.units?.length ?? null;

  return (
    <PtFieldPage title={weld ? `Weld ${weld.weld_number}` : 'Weld'} backTo={jobId ? `/field/ndt/jobs/${jobId}` : '/field/ndt'} testId="ndt-field-weld-screen">
      <PtFieldQueryState query={query} subject="this weld">
        {!weld ? (
          <PtFieldEmpty>This weld was not found on the job.</PtFieldEmpty>
        ) : (
          <>
            <PtFieldCard className="px-4 py-4">
              <div className="flex flex-wrap items-center gap-2">
                <PtFieldBadge skin={ndtWeldStandingSkin(weld.standing)} />
                <span className="font-mono text-sm text-fieldiq-muted">{job?.job_number}</span>
              </div>
              <p className="mt-2 text-base font-bold text-white">
                {weld.welder_name ? `${weld.welder_name}${weld.welder_stamp ? ` (${weld.welder_stamp})` : ''}` : 'No welder recorded'}
              </p>
              <p className="text-sm text-fieldiq-text">
                {formatNdtValue(weld.diameter, unit)} × {formatNdtValue(weld.wall, unit)} · {weld.process_label || weld.process || '—'} · {weld.position || '—'}
              </p>
              <div className="mt-2" data-testid="ndt-field-qual">
                <PtFieldBadge skin={ndtQualStatusSkin(qual?.status)} />
                {qual?.status === 'not_qualified' && (
                  <ul className="mt-1 list-disc pl-5 text-sm text-fieldiq-text">
                    {(qual.reasons ?? []).map((r) => (
                      <li key={r}>{ndtQualReasonText(r)}</li>
                    ))}
                  </ul>
                )}
              </div>
            </PtFieldCard>

            <PtFieldSectionTitle>Record an exam</PtFieldSectionTitle>
            <PtFieldCard className="space-y-3 px-4 py-4">
              <div role="group" aria-label="Method" className="flex flex-wrap gap-2">
                {(cat?.examMethods ?? []).map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    aria-pressed={method === m.value}
                    onClick={() => {
                      setMethod(m.value);
                      setInstrumentId('');
                      if (m.value !== 'RT') setSourceId('');
                    }}
                    className={`h-12 min-w-[64px] rounded-fi-control px-4 text-base font-extrabold ${
                      method === m.value ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-text'
                    }`}
                  >
                    {m.value}
                  </button>
                ))}
              </div>
              {method === 'RT' && (
                <NdtField label="Gamma source" hint="None for X-ray. It must be checked out to you or your crew.">
                  <select className={`${NDT_INPUT} h-12 text-base`} value={sourceId} onChange={(e) => { setSourceId(e.target.value); setInstrumentId(''); }}>
                    <option value="">None (X-ray)</option>
                    {(sources.data?.data ?? []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.serialNumber} · {s.isotopeLabel ?? s.isotope}
                      </option>
                    ))}
                  </select>
                </NdtField>
              )}
              {method && (
                <NdtField label="Instrument" hint={requiredKind ? `A ${ndtKindLabel(requiredKind).toLowerCase()} is required.` : 'Optional for this method.'}>
                  <select className={`${NDT_INPUT} h-12 text-base`} value={instrumentId} onChange={(e) => setInstrumentId(e.target.value)}>
                    <option value="">None</option>
                    {(instruments.data?.data ?? []).map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name ?? '—'}
                        {i.serialNumber ? ` · ${i.serialNumber}` : ''}
                      </option>
                    ))}
                  </select>
                </NdtField>
              )}
              <NdtRefusalNotice refusal={refusal} />
              <NdtWarnings warnings={warnings} />
              <div className="grid grid-cols-3 gap-2">
                {NDT_FIELD_RESULT_BUTTONS.map((b) => (
                  <button key={b.result} type="button" className={`${BIG} ${b.fill}`} disabled={add.isPending} onClick={() => record(b.result)}>
                    {b.label}
                  </button>
                ))}
              </div>
              <button type="button" className={PT_FIELD_SECONDARY} disabled={add.isPending} onClick={() => record('pending')}>
                Shot only — leave pending for a Level II
              </button>
              <p className="text-xs text-fieldiq-muted">Recorded as you, now, on the next repair cycle.</p>
            </PtFieldCard>

            <PtFieldSectionTitle>Exams · {exams.length}</PtFieldSectionTitle>
            {exams.length === 0 ? (
              <PtFieldEmpty>No exam is recorded on this weld yet.</PtFieldEmpty>
            ) : (
              <div className="space-y-2">
                {exams.map((e) => (
                  <PtFieldCard key={e.id} className="px-4 py-3">
                    <div data-testid="ndt-field-exam" className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-extrabold text-white">{e.method}</span>
                      <span className="text-xs text-fieldiq-muted">{ndtCycleLabel(e.cycle)}</span>
                      <PtFieldBadge skin={ndtExamResultSkin(e.result)} />
                    </div>
                    <p className="mt-1 text-xs text-fieldiq-muted">
                      {e.technician_name || '—'} · {ndtLevelLabel(e.technician_level)} · {formatInstant(e.examined_at, meta?.time_zone) ?? '—'}
                    </p>
                  </PtFieldCard>
                ))}
              </div>
            )}
          </>
        )}
      </PtFieldQueryState>
    </PtFieldPage>
  );
}
